import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { MoAvatar } from '@src/components/MoAvatar';
import { MoButton } from '@src/components/MoButton';
import { ChatActorType } from '../types';

type AddParticipantAutocompleteProps = {
  availableParticipants: ChatActorType[];
  disabled: boolean;
  placeholder: string;
  onAdd: (participant: ChatActorType) => void;
  onBlur?: () => void;
};

const INDEPENDENT_GROUP = 'Independent';

function getGroupName(participant: ChatActorType): string {
  return participant.organization?.name ?? INDEPENDENT_GROUP;
}

function isOrganizationAdmin(participant: ChatActorType): boolean {
  return participant.type === 'organization_admin';
}

function AddParticipantAutocomplete({
  availableParticipants,
  disabled,
  placeholder,
  onAdd,
  onBlur,
}: AddParticipantAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');

  // Sort participants: Independent first, then orgs alphabetically, admins last within each group
  const sortedParticipants = useMemo(() => {
    return [...availableParticipants].sort((a, b) => {
      const groupA = getGroupName(a);
      const groupB = getGroupName(b);

      if (groupA === INDEPENDENT_GROUP && groupB !== INDEPENDENT_GROUP) return -1;
      if (groupB === INDEPENDENT_GROUP && groupA !== INDEPENDENT_GROUP) return 1;

      if (groupA !== groupB) return groupA.localeCompare(groupB);

      const aIsAdmin = isOrganizationAdmin(a);
      const bIsAdmin = isOrganizationAdmin(b);
      if (aIsAdmin && !bIsAdmin) return 1;
      if (!aIsAdmin && bIsAdmin) return -1;

      return a.fullName.localeCompare(b.fullName);
    });
  }, [availableParticipants]);

  return (
    <Autocomplete
      options={sortedParticipants}
      getOptionLabel={(option: ChatActorType) => option.fullName}
      groupBy={getGroupName}
      disabled={disabled}
      onChange={(_, option) => {
        if (option) {
          onAdd(option);
          setInputValue('');
        }
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      value={null}
      autoFocus
      openOnFocus
      renderOption={(props, option: ChatActorType) => (
        <Box
          {...props}
          component="li"
          display="flex"
          alignItems="center"
          key={option.id}
          gap={2}
          py={1}
        >
          <MoAvatar size={3} user={option} />
          <Typography>
            {option.fullName}
            {isOrganizationAdmin(option) && (
              <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {' '}
                (Admin)
              </Box>
            )}
          </Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} autoFocus size="small" />
      )}
      noOptionsText="No supervisees available to add"
      onBlur={onBlur}
    />
  );
}

type ChatParticipantEditorProps = {
  selectedParticipants: ChatActorType[];
  availableParticipants: ChatActorType[];
  disabled: boolean;
  autocompletePlaceholder: string;
  onRemove: (participant: ChatActorType) => void;
  onAdd: (participant: ChatActorType) => void;
  alwaysShowAddField?: boolean;
  allowRemoveLastParticipant?: boolean;
  hideAddSection?: boolean;
};

export default function ChatParticipantEditor({
  selectedParticipants,
  availableParticipants,
  disabled,
  autocompletePlaceholder,
  onRemove,
  onAdd,
  alwaysShowAddField = false,
  allowRemoveLastParticipant = false,
  hideAddSection = false,
}: ChatParticipantEditorProps) {
  const [showAddField, setShowAddField] = useState(false);

  const handleAdd = (participant: ChatActorType) => {
    onAdd(participant);
    if (!alwaysShowAddField) {
      setShowAddField(false);
    }
  };

  const canRemove = allowRemoveLastParticipant || selectedParticipants.length > 1;
  const isAddFieldVisible = alwaysShowAddField || showAddField;

  return (
    <Box>
      {selectedParticipants.length > 0 && (
        <List>
          {selectedParticipants.map((participant, index) => (
            <React.Fragment key={participant.id}>
              <ListItem
                disableGutters
                secondaryAction={
                  canRemove && (
                    <MoButton
                      size="small"
                      onClick={() => onRemove(participant)}
                      disabled={disabled}
                      startIcon={<DeleteOutlineIcon />}
                      sx={{ color: 'primary.main' }}
                    >
                      Remove
                    </MoButton>
                  )
                }
                sx={{ py: 2 }}
              >
                <ListItemAvatar sx={{ mr: 2 }}>
                  <MoAvatar size={5} user={participant} />
                </ListItemAvatar>
                <ListItemText primary={participant.fullName} />
              </ListItem>
              {index < selectedParticipants.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}

      {selectedParticipants.length > 0 && !hideAddSection && <Divider />}

      {!hideAddSection && (
        <Box sx={{ py: 2 }}>
          {isAddFieldVisible ? (
            <AddParticipantAutocomplete
              availableParticipants={availableParticipants}
              disabled={disabled}
              placeholder={autocompletePlaceholder}
              onAdd={handleAdd}
              onBlur={alwaysShowAddField ? undefined : () => setShowAddField(false)}
            />
          ) : (
            <MoButton
              startIcon={<AddIcon />}
              onClick={() => setShowAddField(true)}
              disabled={disabled || availableParticipants.length === 0}
              sx={{ color: 'primary.main' }}
            >
              Add another supervisee
            </MoButton>
          )}
        </Box>
      )}
    </Box>
  );
}
